import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Grid,
  Paper,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  CalendarToday,
  Person,
  Receipt,
  Edit,
  Download,
  Email,
} from "@mui/icons-material";
import { generateInvoicePDF } from "../../../utils/pdfGenerator";
import { formatDateDDMMYYYY } from "../../../utils/dateUtils";

const InvoiceDetailDrawer = ({ open, onClose, invoiceData, onEdit }) => {
  if (!invoiceData) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return formatDateDDMMYYYY(dateString);
  };

  const getStatusColor = (status) => {
    return status === "Paid" ? "success" : "error";
  };

  const handleDownloadPDF = () => {
    try {
      generateInvoicePDF(invoiceData);
    } catch (error) {
      console.error("Error generating PDF:", error);
      // You can add a snackbar or alert here to show error to user
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: "75%",
          maxWidth: "1200px",
          bgcolor: "#f8f9fa",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: "white",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{ color: "#000000" }}
            >
              INVOICE
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: "#666666" }}>
              Invoice Details
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#666666" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
          {/* Invoice Header Information */}
          <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: "white" }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    gutterBottom
                    sx={{ color: "#000000" }}
                  >
                    Invoice Information
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Receipt sx={{ mr: 1, color: "#1976d2" }} />
                    <Typography variant="body2" sx={{ color: "#666666" }}>
                      Invoice Number
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight="500"
                    sx={{ color: "#000000" }}
                  >
                    {invoiceData.invoice_number}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <CalendarToday sx={{ mr: 1, color: "#1976d2" }} />
                    <Typography variant="body2" sx={{ color: "#666666" }}>
                      Invoice Date
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: "#000000" }}>
                    {formatDate(invoiceData.Invoice_date)}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <CalendarToday sx={{ mr: 1, color: "#d32f2f" }} />
                    <Typography variant="body2" sx={{ color: "#666666" }}>
                      Due Date
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: "#000000" }}>
                    {formatDate(invoiceData.due_date)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    gutterBottom
                    sx={{ color: "#000000" }}
                  >
                    Status & Payment
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={invoiceData.status}
                      color={getStatusColor(invoiceData.status)}
                      variant="filled"
                      sx={{ fontWeight: "600", fontSize: "0.875rem" }}
                    />
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: "#666666" }}>
                    Total Amount
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight="600"
                    sx={{ color: "#1976d2" }}
                  >
                    {formatCurrency(invoiceData.total_amount)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: "#666666" }}>
                    Paid Amount
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#2e7d32" }}>
                    {formatCurrency(invoiceData.paid_amount)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ color: "#666666" }}>
                    Outstanding Amount
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#d32f2f" }}>
                    {formatCurrency(invoiceData.unpaid_amount)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Client Information */}
          <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: "white" }}>
            <Typography
              variant="h6"
              fontWeight="600"
              gutterBottom
              sx={{ color: "#000000" }}
            >
              <Person
                sx={{ mr: 1, verticalAlign: "middle", color: "#1976d2" }}
              />
              Billed To
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: "#666666" }}>
                Client ID
              </Typography>
              <Typography
                variant="body1"
                fontWeight="500"
                sx={{ color: "#000000" }}
              >
                {invoiceData.client_id}
              </Typography>
            </Box>

            {invoiceData.client_name && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: "#666666" }}>
                  Client Name
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{ color: "#000000" }}
                >
                  {invoiceData.client_name}
                </Typography>
              </Box>
            )}

            {invoiceData.shipping_address && (
              <Box>
                <Typography variant="body2" sx={{ color: "#666666" }}>
                  Shipping Address
                </Typography>
                <Typography variant="body1" sx={{ color: "#000000" }}>
                  {invoiceData.shipping_address}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Invoice Items */}
          {invoiceData.items && invoiceData.items.length > 0 && (
            <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: "white" }}>
              <Typography
                variant="h6"
                fontWeight="600"
                gutterBottom
                sx={{ color: "#000000" }}
              >
                Invoice Items
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                          color: "#000000",
                          fontWeight: "600",
                        }}
                      >
                        Description
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          borderBottom: "1px solid #ddd",
                          color: "#000000",
                          fontWeight: "600",
                        }}
                      >
                        Quantity
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          borderBottom: "1px solid #ddd",
                          color: "#000000",
                          fontWeight: "600",
                        }}
                      >
                        Unit Price
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          borderBottom: "1px solid #ddd",
                          color: "#000000",
                          fontWeight: "600",
                        }}
                      >
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.items.map((item, index) => (
                      <tr key={index}>
                        <td
                          style={{
                            padding: "12px",
                            borderBottom: "1px solid #eee",
                            color: "#000000",
                          }}
                        >
                          {item.description || item.name || "Item"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            borderBottom: "1px solid #eee",
                            color: "#000000",
                          }}
                        >
                          {item.quantity || item.qty || 1} {item.unit || "Pcs"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "right",
                            borderBottom: "1px solid #eee",
                            color: "#000000",
                          }}
                        >
                          {formatCurrency(item.unit_price || item.price || 0)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "right",
                            borderBottom: "1px solid #eee",
                            color: "#000000",
                          }}
                        >
                          {formatCurrency(
                            (item.quantity || 1) *
                              (item.unit_price || item.price || 0)
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              {/* Invoice Totals */}
              <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #eee" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}></Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">Sub Total:</Typography>
                      <Typography variant="body2" fontWeight="500">
                        {formatCurrency(
                          invoiceData.total_amount + (invoiceData.discount || 0)
                        )}
                      </Typography>
                    </Box>
                    {invoiceData.discount && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "#2e7d32" }}>
                          Discount:
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#2e7d32" }}>
                          -{formatCurrency(invoiceData.discount)}
                        </Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="600"
                        sx={{ color: "#000000" }}
                      >
                        Total:
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="600"
                        sx={{ color: "#1976d2" }}
                      >
                        {formatCurrency(invoiceData.total_amount)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          )}

          {/* Additional Details */}
          <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: "white" }}>
            <Typography
              variant="h6"
              fontWeight="600"
              gutterBottom
              sx={{ color: "#000000" }}
            >
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {invoiceData.Generated_by && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: "#666666" }}>
                      Generated By
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#000000" }}>
                      {invoiceData.Generated_by}
                    </Typography>
                  </Box>
                )}

                {invoiceData.created_by && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: "#666666" }}>
                      Created By
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#000000" }}>
                      {invoiceData.created_by}
                    </Typography>
                  </Box>
                )}

                {invoiceData.authorized_by && (
                  <Box>
                    <Typography variant="body2" sx={{ color: "#666666" }}>
                      Authorized By
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#000000" }}>
                      {invoiceData.authorized_by}
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                {invoiceData.paid_date && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: "#666666" }}>
                      Paid Date
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#000000" }}>
                      {formatDate(invoiceData.paid_date)}
                    </Typography>
                  </Box>
                )}

                {invoiceData.created_at && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: "#666666" }}>
                      Created At
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#000000" }}>
                      {formatDate(invoiceData.created_at)}
                    </Typography>
                  </Box>
                )}

                {invoiceData.updated_at && (
                  <Box>
                    <Typography variant="body2" sx={{ color: "#666666" }}>
                      Last Updated
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#000000" }}>
                      {formatDate(invoiceData.updated_at)}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>

            {invoiceData.note && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ color: "#666666" }}>
                  Notes
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, color: "#000000" }}>
                  {invoiceData.note}
                </Typography>
              </Box>
            )}

            {invoiceData.terms && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ color: "#666666" }}>
                  Terms & Conditions
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, color: "#000000" }}>
                  {invoiceData.terms}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            p: 3,
            bgcolor: "white",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={onEdit}
            sx={{
              borderColor: "#4285F4",
              color: "#4285F4",
              "&:hover": {
                bgcolor: "#4285F4",
                color: "white",
              },
            }}
          >
            Edit Invoice
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownloadPDF}
            sx={{
              borderColor: "#34A853",
              color: "#34A853",
              "&:hover": {
                bgcolor: "#34A853",
                color: "white",
              },
            }}
          >
            Download PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<Email />}
            sx={{
              borderColor: "#EA4335",
              color: "#EA4335",
              "&:hover": {
                bgcolor: "#EA4335",
                color: "white",
              },
            }}
          >
            Send Email
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default InvoiceDetailDrawer;
